import React, { useState, useEffect } from 'react';
import { webhooks } from '../api';
import { Webhook, Loader2, RefreshCw, CheckCircle, XCircle, Clock, AlertTriangle, Phone, PhoneOff, Search, RotateCcw, BarChart3, ChevronLeft, ChevronRight } from 'lucide-react';
import LoadingSpinner from './LoadingSpinner';
import AnimatedDropdown from './AnimatedDropdown';

const WebhookEvents = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statistics, setStatistics] = useState({
    pending: 0,
    success: 0,
    failed: 0,
    abandoned: 0
  });
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 0
  });
  const [filters, setFilters] = useState({
    eventType: '',
    status: ''
  });
  const [autoRefresh, setAutoRefresh] = useState(true);

  const eventTypes = [
    { value: '', label: 'All Events' },
    { value: 'call_started', label: 'Call Started' },
    { value: 'call_ended', label: 'Call Ended' },
    { value: 'call_analyzed', label: 'Call Analyzed' },
    { value: 'call_transferred', label: 'Call Transferred' },
    { value: 'chat_started', label: 'Chat Started' },
    { value: 'chat_ended', label: 'Chat Ended' },
    { value: 'chat_analyzed', label: 'Chat Analyzed' }
  ];

  const statusTypes = [
    { value: '', label: 'All Statuses' },
    { value: 'pending', label: 'Pending' },
    { value: 'success', label: 'Success' },
    { value: 'failed', label: 'Failed' },
    { value: 'abandoned', label: 'Abandoned' }
  ];

  useEffect(() => {
    loadEvents();
    loadStatistics();
  }, [pagination.page, filters]);

  useEffect(() => {
    if (!autoRefresh) return;

    const interval = setInterval(() => {
      loadEvents();
      loadStatistics();
    }, 5000); // Refresh every 5 seconds

    return () => clearInterval(interval);
  }, [autoRefresh, pagination.page, filters]);

  const loadEvents = async () => {
    try {
      setLoading(true);
      const response = await webhooks.listEvents(pagination.page, pagination.limit, filters);
      setEvents(response.data.events || []);
      setPagination(prev => ({
        ...prev,
        total: response.data.total || 0,
        totalPages: response.data.total_pages || 0
      }));
    } catch (err) {
      console.error('Failed to load webhook events:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadStatistics = async () => {
    try {
      const response = await webhooks.getStatistics();
      setStatistics(response.data.statistics || {
        pending: 0,
        success: 0,
        failed: 0,
        abandoned: 0
      });
    } catch (err) {
      console.error('Failed to load webhook statistics:', err);
    }
  };

  const handlePageChange = (newPage) => {
    setPagination(prev => ({ ...prev, page: newPage }));
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPagination(prev => ({ ...prev, page: 1 })); // Reset to first page
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString();
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'success': return 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30';
      case 'failed': return 'bg-red-500/20 text-red-400 border-red-500/30';
      case 'pending': return 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30';
      case 'abandoned': return 'bg-gray-500/20 text-gray-400 border-gray-500/30';
      default: return 'bg-gray-500/20 text-gray-400 border-gray-500/30';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'success': return <CheckCircle className="w-4 h-4" />;
      case 'failed': return <XCircle className="w-4 h-4" />;
      case 'pending': return <Clock className="w-4 h-4" />;
      case 'abandoned': return <AlertTriangle className="w-4 h-4" />;
      default: return <AlertTriangle className="w-4 h-4" />;
    }
  };

  const getEventTypeIcon = (eventType) => {
    switch (eventType) {
      case 'call_started': return <Phone className="w-4 h-4" />;
      case 'call_ended': return <PhoneOff className="w-4 h-4" />;
      case 'call_analyzed': return <Search className="w-4 h-4" />;
      case 'call_transferred': return <RotateCcw className="w-4 h-4" />;
      case 'chat_started': return <Phone className="w-4 h-4" />;
      case 'chat_ended': return <PhoneOff className="w-4 h-4" />;
      case 'chat_analyzed': return <Search className="w-4 h-4" />;
      default: return <Webhook className="w-4 h-4" />;
    }
  };

  const truncateUrl = (url, maxLength = 40) => {
    if (!url) return 'N/A';
    return url.length > maxLength ? url.substring(0, maxLength) + '...' : url;
  };

  return (
    <div className="flex-1 flex flex-col min-h-0">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-2">
          <div>
            <h1 className="text-3xl font-bold text-foreground mb-2">Webhook Events</h1>
            <p className="text-muted-foreground">Monitor webhook delivery and events</p>
          </div>
          <div className="flex items-center gap-4">
            <label className="flex items-center gap-2 text-sm text-foreground cursor-pointer">
            <input
              type="checkbox"
              checked={autoRefresh}
              onChange={(e) => setAutoRefresh(e.target.checked)}
              className=""
            />
            Auto-refresh
          </label>
          <button
            onClick={loadEvents}
              className="flex items-center gap-2 px-4 py-2 bg-gradient-button text-primary-foreground rounded-lg hover:shadow-lg hover:shadow-primary/20 transition-all duration-300 btn-hover-gradient"
          >
              <RefreshCw className="w-4 h-4" />
              Refresh
          </button>
          </div>
        </div>
      </div>

      <div className="flex-1 flex flex-col min-h-0 space-y-6 overflow-y-auto">
        {/* Statistics */}
        <div className="rounded-xl border border-border/60 bg-card/80 backdrop-blur-sm p-4 shadow-elegant">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-base font-semibold text-foreground flex items-center gap-2">
              <BarChart3 className="w-4 h-4" />
              Statistics
            </h3>
            <div className="flex items-center gap-4">
              <div>
                <label className="block text-xs font-medium text-muted-foreground mb-1">
                  Event Type
                </label>
                <AnimatedDropdown
                  value={filters.eventType}
                  onChange={(value) => handleFilterChange('eventType', value)}
                  options={eventTypes}
                  placeholder="All Events"
                  className="w-48"
                />
              </div>
              
              <div>
                <label className="block text-xs font-medium text-muted-foreground mb-1">
                  Status
                </label>
                <AnimatedDropdown
                  value={filters.status}
                  onChange={(value) => handleFilterChange('status', value)}
                  options={statusTypes}
                  placeholder="All Statuses"
                  className="w-48"
                />
              </div>
            </div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {statusTypes.slice(1).map((statusType) => {
            const count = statistics[statusType.value] || 0;
            return (
                <div key={statusType.value} className="text-center p-3 rounded-lg bg-background/30 border border-border/40">
                  <div className={`text-xl font-bold ${getStatusColor(statusType.value).split(' ')[1]}`}>
                  {count}
                </div>
                  <div className="text-xs text-muted-foreground">{statusType.label}</div>
              </div>
            );
          })}
        </div>
          <div className="mt-3 pt-3 border-t border-border/40">
            <div className="flex items-center justify-between text-sm text-muted-foreground">
              <div>
                Total: <span className="text-foreground font-semibold">{pagination.total}</span> events
              </div>
              <div>
                Page: <span className="text-foreground font-semibold">{pagination.page}</span> of <span className="text-foreground font-semibold">{pagination.totalPages}</span>
              </div>
            </div>
          </div>
      </div>

      {/* Events List */}
      {loading ? (
        <div className="flex justify-center items-center h-64">
            <LoadingSpinner size="lg" text="Loading webhook events..." />
        </div>
      ) : events.length === 0 ? (
          <div className="text-center py-12 rounded-xl border border-border/60 bg-card/80 backdrop-blur-sm">
            <Webhook className="w-12 h-12 mx-auto mb-3 text-muted-foreground opacity-50" />
            <p className="text-muted-foreground">No webhook events found</p>
            <p className="text-sm text-muted-foreground mt-2">Events will appear here when webhooks are triggered</p>
        </div>
      ) : (
          <div className="space-y-2">
          {events.map((event) => (
            <div
              key={event.id}
                className="rounded-xl border border-border/60 bg-card/80 backdrop-blur-sm p-4 hover:bg-card hover:border-primary/20 transition-all shadow-elegant"
            >
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <div className={`flex items-center justify-center w-8 h-8 rounded-lg ${getStatusColor(event.status)}`}>
                      {getEventTypeIcon(event.event_type)}
                    </div>
                  <div>
                      <h3 className="text-foreground font-semibold text-sm">{event.event_type}</h3>
                      <p className="text-xs text-muted-foreground">ID: {event.webhook_id}</p>
                  </div>
                </div>
                
                <div className="text-right">
                    <div className="flex items-center justify-end gap-2">
                      <span className={`flex items-center gap-1 px-2 py-1 rounded-md text-xs border ${getStatusColor(event.status)}`}>
                        {getStatusIcon(event.status)}
                        {event.status}
                    </span>
                      <span className="text-xs text-muted-foreground">
                      {event.attempts} attempt{event.attempts !== 1 ? 's' : ''}
                    </span>
                  </div>
                </div>
              </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-2 mb-2">
                  <div className="p-2 rounded-lg bg-background/30 border border-border/40">
                    <p className="text-xs text-muted-foreground mb-0.5">Target URL</p>
                    <p className="text-xs text-foreground font-mono truncate" title={event.target_url}>
                    {truncateUrl(event.target_url)}
                  </p>
                </div>
                
                  <div className="p-2 rounded-lg bg-background/30 border border-border/40">
                    <p className="text-xs text-muted-foreground mb-0.5">Session ID</p>
                    <p className="text-xs text-foreground font-mono break-all">
                      {event.session_id || 'N/A'}
                  </p>
                </div>
                
                  <div className="p-2 rounded-lg bg-background/30 border border-border/40">
                    <p className="text-xs text-muted-foreground mb-0.5">Created</p>
                    <p className="text-xs text-foreground">{formatDate(event.created_at)}</p>
                </div>
                
                  <div className="p-2 rounded-lg bg-background/30 border border-border/40">
                    <p className="text-xs text-muted-foreground mb-0.5">Completed</p>
                    <p className="text-xs text-foreground">{formatDate(event.completed_at)}</p>
                </div>
              </div>

              {event.response_status_code && (
                  <div className="mb-2 p-2 rounded-lg bg-background/30 border border-border/40">
                    <p className="text-xs text-muted-foreground mb-0.5">Response</p>
                    <p className={`text-xs font-semibold ${
                    event.response_status_code >= 200 && event.response_status_code < 300
                        ? 'text-success'
                        : 'text-destructive'
                  }`}>
                    HTTP {event.response_status_code}
                  </p>
                </div>
              )}

              {event.error_message && (
                  <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-2">
                    <p className="text-xs text-muted-foreground mb-0.5">Error Message</p>
                    <p className="text-xs text-destructive">{event.error_message}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Pagination */}
      {pagination.totalPages > 1 && (
          <div className="flex justify-center items-center gap-4">
          <button
            onClick={() => handlePageChange(pagination.page - 1)}
            disabled={pagination.page <= 1}
              className="flex items-center gap-2 px-4 py-2 bg-gradient-button text-primary-foreground rounded-lg hover:shadow-lg hover:shadow-primary/20 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
          >
              <ChevronLeft className="w-4 h-4" />
            Previous
          </button>
          
            <div className="flex gap-2">
            {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
              const pageNum = i + 1;
              return (
                <button
                  key={pageNum}
                  onClick={() => handlePageChange(pageNum)}
                    className={`px-3 py-2 rounded-lg transition-all ${
                    pagination.page === pageNum
                        ? 'bg-gradient-button text-primary-foreground shadow-lg shadow-primary/20'
                        : 'bg-background/50 border border-border/60 text-foreground hover:bg-background hover:border-primary/20'
                  }`}
                >
                  {pageNum}
                </button>
              );
            })}
          </div>
          
          <button
            onClick={() => handlePageChange(pagination.page + 1)}
            disabled={pagination.page >= pagination.totalPages}
              className="flex items-center gap-2 px-4 py-2 bg-gradient-button text-primary-foreground rounded-lg hover:shadow-lg hover:shadow-primary/20 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Next
              <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      )}
      </div>
    </div>
  );
};

export default WebhookEvents;
